import type { Ref } from 'vue';

import { resolveInactivitySessionActiveUntil } from './session-deadline';
import { resolveAuthSessionTokenClaims } from './token-claims';
import type {
  AuthSessionStatus,
  EnsureSessionOptions,
  EnsureSessionResult,
  UserContextStoreLike,
} from './types';

type AuthSessionStateDependencies = {
  status: Ref<AuthSessionStatus>;
  syncError: Ref<unknown>;
  jwtToken: Ref<string | null>;
  idToken: Ref<string | null>;
  lastActiveTime: Ref<number>;
  sessionActiveUntil: Ref<number | null>;
  tokenExpiresAt: Ref<number | null>;
  ifExternalUserId: Ref<string | null>;
  ifClientId: Ref<string | null>;
  givenName: Ref<string | null>;
  familyName: Ref<string | null>;
  organizationId: Ref<string | null>;
  isTokenExpired: Ref<boolean>;
  userContextStore: UserContextStoreLike;
};

export type AuthSessionSnapshot = {
  jwtToken: string | null;
  idToken: string | null;
  tokenExpiresAt: number | null;
  sessionActiveUntil: number | null;
  ifExternalUserId: string | null;
  ifClientId: string | null;
  givenName: string | null;
  familyName: string | null;
  organizationId: string | null;
  lastActiveTime: number;
};

export const createAuthSessionState = ({
  status,
  syncError,
  jwtToken,
  idToken,
  lastActiveTime,
  sessionActiveUntil,
  tokenExpiresAt,
  ifExternalUserId,
  ifClientId,
  givenName,
  familyName,
  organizationId,
  isTokenExpired,
  userContextStore,
}: AuthSessionStateDependencies) => {
  const setStatus = (nextStatus: AuthSessionStatus, error: unknown = null) => {
    status.value = nextStatus;
    syncError.value = error;
  };

  const setToken = (token: string) => {
    const now = Date.now();
    const claims = resolveAuthSessionTokenClaims({
      accessToken: token,
      idToken: idToken.value,
    });

    jwtToken.value = token;
    tokenExpiresAt.value = claims.tokenExpiresAt;

    if (claims.tokenExpiresAt !== null) {
      sessionActiveUntil.value = claims.tokenExpiresAt;
    } else if (sessionActiveUntil.value === null) {
      sessionActiveUntil.value = resolveInactivitySessionActiveUntil({ now });
    }

    ifExternalUserId.value = claims.ifExternalUserId;
    ifClientId.value = claims.ifClientId;
    givenName.value = claims.givenName;
    familyName.value = claims.familyName;
    lastActiveTime.value = now;
  };

  const setIdToken = (token: string | null) => {
    const claims = resolveAuthSessionTokenClaims({
      accessToken: jwtToken.value,
      idToken: token,
    });

    idToken.value = token;
    organizationId.value = claims.organizationId;
    givenName.value = claims.givenName;
    familyName.value = claims.familyName;
    userContextStore.syncSelectedOrganizationFromToken(claims.organizationId);
  };

  const clearAuth = () => {
    jwtToken.value = null;
    idToken.value = null;
    tokenExpiresAt.value = null;
    sessionActiveUntil.value = null;
    ifExternalUserId.value = null;
    ifClientId.value = null;
    givenName.value = null;
    familyName.value = null;
    organizationId.value = null;
    userContextStore.reset();
    setStatus('anonymous');
  };

  const getSessionResultFromState = (
    options?: EnsureSessionOptions,
  ): Extract<EnsureSessionResult, { status: 'authenticated' }> | null => {
    const reason = options?.reason ?? 'request';

    if (!jwtToken.value || isTokenExpired.value) {
      return null;
    }

    if (!idToken.value) {
      return null;
    }

    if (options?.targetOrganizationId && organizationId.value !== options.targetOrganizationId) {
      return null;
    }

    if (!userContextStore.isLoaded) {
      return null;
    }

    return {
      status: 'authenticated',
      accessToken: jwtToken.value,
      idToken: idToken.value,
      reason,
    };
  };

  const setSession = ({
    accessToken,
    nextIdToken,
  }: {
    accessToken: string;
    nextIdToken: string;
  }) => {
    setToken(accessToken);
    setIdToken(nextIdToken);
  };

  const captureSessionSnapshot = (): AuthSessionSnapshot => {
    return {
      jwtToken: jwtToken.value,
      idToken: idToken.value,
      tokenExpiresAt: tokenExpiresAt.value,
      sessionActiveUntil: sessionActiveUntil.value,
      ifExternalUserId: ifExternalUserId.value,
      ifClientId: ifClientId.value,
      givenName: givenName.value,
      familyName: familyName.value,
      organizationId: organizationId.value,
      lastActiveTime: lastActiveTime.value,
    };
  };

  const restoreSessionSnapshot = (snapshot: AuthSessionSnapshot) => {
    jwtToken.value = snapshot.jwtToken;
    idToken.value = snapshot.idToken;
    tokenExpiresAt.value = snapshot.tokenExpiresAt;
    sessionActiveUntil.value = snapshot.sessionActiveUntil;
    ifExternalUserId.value = snapshot.ifExternalUserId;
    ifClientId.value = snapshot.ifClientId;
    givenName.value = snapshot.givenName;
    familyName.value = snapshot.familyName;
    organizationId.value = snapshot.organizationId;
    lastActiveTime.value = snapshot.lastActiveTime;
    userContextStore.syncSelectedOrganizationFromToken(snapshot.organizationId);
  };

  return {
    setStatus,
    setToken,
    setIdToken,
    clearAuth,
    getSessionResultFromState,
    setSession,
    captureSessionSnapshot,
    restoreSessionSnapshot,
  };
};
